import { useEffect, useState, type ReactNode } from 'react';
import { ANNOUNCEMENTS, type Politeness } from '@mailbutler/ux';

/**
 * What the product says out loud.
 *
 * Two regions rather than one, and that is the whole design. A single region has to pick a
 * politeness, and picking `assertive` makes the application interrupt itself over a save
 * confirmation while picking `polite` means a failure waits behind whatever is being read.
 *
 * Which kind of announcement goes where is `@mailbutler/ux`, so a new announcement is a line in a
 * table rather than a judgement made in a component at four in the afternoon.
 */

export interface Announcement {
  /*
   * A plain string rather than the union of known kinds.
   *
   * `ANNOUNCEMENTS` is a record keyed by string, so a union with `keyof` would be the same type
   * twice — and more usefully, a caller announcing something the table has not heard of should get
   * the polite default rather than a compile error, because the failure of announcing politely is
   * nothing at all.
   */
  readonly kind: string;
  readonly text: string;
}

let announce: ((announcement: Announcement) => void) | null = null;

/** Called from anywhere. A module-level function rather than context, so a toast can use it. */
export function say(announcement: Announcement): void {
  announce?.(announcement);
}

function politenessOf(kind: string): Politeness {
  /* An unknown kind is polite rather than assertive. Interrupting somebody is the thing that has to
     be asked for explicitly. */
  return ANNOUNCEMENTS[kind] ?? 'polite';
}

export function LiveRegion(): ReactNode {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');

  useEffect(() => {
    announce = (announcement) => {
      const politeness = politenessOf(announcement.kind);
      if (politeness === 'off') return;

      /*
       * Cleared before being set.
       *
       * A screen reader announces a change to the region's content, so setting the same text twice
       * announces nothing the second time — which is exactly the case of two identical failures in
       * a row, where the second one matters most.
       */
      const set = politeness === 'assertive' ? setAssertive : setPolite;
      set('');
      requestAnimationFrame(() => set(announcement.text));
    };

    return () => {
      announce = null;
    };
  }, []);

  return (
    <>
      <div aria-live="polite" aria-atomic="true" className="visually-hidden" role="status">
        {polite}
      </div>
      <div aria-live="assertive" aria-atomic="true" className="visually-hidden" role="alert">
        {assertive}
      </div>
    </>
  );
}
